<template>
  <div
    class="w-full min-h-screen pb-8 overflow-y-auto bg-cover"
    :style="'background-image: url(' + BackgroundImage + ')'"
  >
    <div class="flex justify-center flex-1 mt-6 px-5">
      <div class="w-full max-w-100">
        <div
          class="w-full rounded-lg bg-white dark:bg-slate-900 px-5 py-8 base-shadow sm:px-6 md:px-8"
        >
        <!-- <h1 class="text-2xl font-bold text-center text-primary font-manrope">Beesphone</h1> -->
        <img src="/images/logo.png" alt="Logo" class="w-32 mb-6 mx-auto dark:invert dark:brightness-0">

        <div class="flex items-center mt-6">
          <NuxtLink
            to="signin"
            class="w-full pt-3 pb-1 font-medium text-center border-b-2 border-b-primary text-[#182233] dark:text-slate-100 font-manrope hover:opacity-80 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Sign In
          </NuxtLink>
          <NuxtLink
            to="signup"
            class="border-b border-b-neutral-300 dark:border-b-slate-700 w-full pt-3 pb-1 font-medium text-center text-[#182233] dark:text-slate-400 font-manrope hover:opacity-80 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Sign Up
          </NuxtLink>
        </div>

        <form
          class="mt-6"
          action=""
          method="post"
          @submit.prevent="handleSubmit"
        >
          <InputFields
            v-model="emailAddress"
            label="Email address"
            placeholder="Enter your email"
            :left-icon="HiOutlineMail"
            required
            type="email"
          />
          <SecureInput
            v-model="password"
            label="Password"
            placeholder="Enter your password"
            :left-icon="LuLock"
            required
            class="mt-4"
          />

          <NuxtLink
            to="forgot-password"
            class="block mt-2 ml-auto text-sm font-medium max-w-max text-danger font-manrope hover:bg-slate-50 hover:opacity-70"
          >
            Forgot password?
          </NuxtLink>

          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <AlertMessage v-if="message" :type="notificationError" :message="message" class="mt-4">
              <AlertIcon class="w-5 h-auto mr-3 min-w-5 shrink-0" stroke-class="" />
            </AlertMessage>
          </Transition>

          <Button
            variant="primary"
            :title="isAuthenticating ? 'Signing In...' : 'Sign In'"
            size="lg"
            :disabled="isAuthenticating"
            class="w-full mt-6"
            type="submit"
            :icon="HiOutlineArrowRight"
            icon-after-title
          />
        </form>

        <div class="flex items-center justify-center mt-6">
          <div class="h-px w-full bg-[#D9D9D9] dark:bg-slate-700" />
          <span class="px-4 text-sm font-medium text-[#39404F] dark:text-slate-400 font-manrope"
            >OR</span
          >
          <div class="h-px w-full bg-[#D9D9D9] dark:bg-slate-700" />
        </div>

        <Button
          variant="secondary"
          :title="isAuthenticating ? 'Signing in...' : 'Continue with Google'"
          size="lg"
          :disabled="isAuthenticating"
          class="w-full mt-6"
          icon="icons8-google"
          @click="onSignInWithGoogleButtonClick"
        >
          <template #icon>
            <img
              src="/images/google.png"
              alt="Google SignIn"
              class="mr-1 size-5"
            >
          </template>
        </Button>
        <Button
          variant="secondary"
          title="Continue with Apple"
          size="lg"
          class="w-full mt-2 opacity-50 cursor-not-allowed"
          icon="icons8-apple"
          @click="onSignInWithAppleButtonClick"
        >
          <template #icon>
            <img
              src="/images/apple.png"
              alt="Apple SignIn"
              class="mr-1 size-5 dark:invert dark:brightness-0"
            >
          </template>
        </Button>

        <p
          class="mt-8 text-center text-sm font-medium text-[#39404F] dark:text-slate-400 font-manrope"
        >
          Download The Beesphone App
        </p>

        <div class="flex items-center justify-center mt-6">
          <Button
            variant="secondary"
            size="lg"
            class="w-full mr-2"
            title=""
            @click="onPlayStoreButtonClick"
          >
            <!-- Normal layout for Light Mode -->
            <img alt="Play Store" src="/images/play-store.png" class="h-6 dark:hidden">
            <!-- Composite layering for Dark Mode: White Text + Colored Logo -->
            <div class="h-6 relative hidden dark:flex">
              <!-- Brightness inverted makes the dark text white -->
              <img alt="" src="/images/play-store.png" class="h-6 invert brightness-0">
              <!-- Original image overlays just the colored triangle -->
              <img alt="" src="/images/play-store.png" class="h-6 absolute left-0 top-0" style="clip-path: inset(0 72% 0 0)">
            </div>
          </Button>
          <Button
            variant="secondary"
            size="lg"
            class="w-full ml-2"
            title=""
            @click="onAppStoreButtonClick"
          >
            <!-- Apple badge is monochromatic, safely inverted for dark mode text readability -->
            <img alt="App Store" src="/images/app-store.png" class="h-6 dark:invert dark:brightness-0">
          </Button>
        </div>
        </div>

        <div class="mt-4 w-full text-center">
          <AppVersionBadge tone="auth" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SecureInput from "~/components/common/SecureInput.vue";
import Button from "~/components/common/Button.vue";
import InputFields from "~/components/common/InputFields.vue";
import AlertMessage from "~/components/common/AlertMessage.vue";
import AlertIcon from "~/components/icons/AlertIcon.vue";
import BackgroundImage from "~/assets/images/bg-auth.png";
import { signIn, signInWithGoogle } from "~/services/authentication";
import { authenticationErrorMap } from "~/constants/authenticationErrorMap";
import { logEvent, type Analytics } from "firebase/analytics";

import { HiOutlineMail, HiOutlineArrowRight } from "vue3-icons/hi";
import { LuLock } from "vue3-icons/lu";

definePageMeta({
  layout: "auth",
  title: "Sign In",
});

const route = useRoute();
const redirectTo = route.query.redirectTo || '/';

const isAuthenticating = ref(false);
const emailAddress = ref("");
const password = ref("");
const message = ref<string>("");
const notificationError = ref<"info" | "success" | "warning" | "error">(
  "success"
);

const handleSubmit = async () => {
  isAuthenticating.value = true;
  try {
    await signIn({
      emailAddress: emailAddress.value,
      password: password.value,
    }, redirectTo as string);
  } catch (error) {
    // @ts-expect-error error.code may not be a key of authenticationErrorMap
    message.value = authenticationErrorMap[error.code as string] || error.code;
    notificationError.value = "error";
  } finally {
    isAuthenticating.value = false;
    setTimeout(() => {
      message.value = "";
    }, 2000);
  }
};

const onAppStoreButtonClick = () => {
  console.log("onAppStoreButtonClick");
};

const onPlayStoreButtonClick = () => {
  console.log("onPlayStoreButtonClick");
};

const onSignInWithGoogleButtonClick = async () => {
  if (isAuthenticating.value) return;

  isAuthenticating.value = true;
  try {
    await signInWithGoogle(redirectTo as string);
  } catch (error) {
    const authError = error as { code?: string; message?: string };
    message.value =
      authenticationErrorMap[authError.code as keyof typeof authenticationErrorMap] ||
      authError.message ||
      authError.code ||
      "Unable to sign in with Google.";
    notificationError.value = "error";
  } finally {
    isAuthenticating.value = false;
    setTimeout(() => {
      message.value = "";
    }, 2000);
  }
};

const _onSignInWithFacebookButtonClick = () => {
  console.log("onSignInWithFacebookButtonClick");
};

const onSignInWithAppleButtonClick = () => {
  // Apple sign-in is not yet enabled. Track taps to measure pre-launch interest.
  const analytics = useNuxtApp().$analytics as Analytics | undefined;
  if (analytics) {
    logEvent(analytics, 'apple_signin_interest', { source: 'signin' });
  }
};
</script>
